/**
 * portfolio DB ファイルの remote 同期で使う共通ロジック。
 * token 検証、API エラー生成、DB ファイル upload を提供する。
 */
import { apiEndpoint } from '@/environment'
import { compress } from '@/utils'
import { AuthTokenExpiredException } from '@pkg/basic'
import {
  type DbSyncTelemetry,
  measureDbSyncTelemetryPhase,
} from './dbSyncTelemetry'

/**
 * API レスポンスから詳細メッセージつき Error を生成する。
 *
 * @param params - action 名とレスポンス
 * @returns 生成した Error
 */
export const createApiError = async (params: {
  action: string
  response: Response
}): Promise<Error> => {
  const { action, response } = params
  const responseBody = await response.text()
  const bodyText = responseBody.length === 0 ? '' : `: ${responseBody}`
  return new Error(
    `Failed to ${action}: ${response.status} ${response.statusText}${bodyText}`,
  )
}

/**
 * 認証 token を取得し、未認証時は例外を送出する。
 *
 * @param getToken - token 取得関数
 * @returns Bearer token
 */
export const requireToken = async (
  getToken: () => Promise<string | null>,
): Promise<string> => {
  const token = await getToken()
  if (token === null) {
    throw new AuthTokenExpiredException()
  }
  return token
}

/**
 * portfolio DB ファイルを remote へ upload する。
 * development では API へ直接送信し、本番では presign URL 経由で upload する。
 *
 * @param params - upload 実行に必要な引数
 */
export const uploadPortfolioDbFileToRemote = async (params: {
  portfolioId: string
  timestamp: number
  getToken: () => Promise<string | null>
  file: File
  telemetry?: DbSyncTelemetry
}): Promise<void> => {
  const { portfolioId, timestamp, getToken, file, telemetry } = params
  const gzipFile = await measureDbSyncTelemetryPhase({
    telemetry,
    phase: 'db-backup.compress',
    task: () => compress(file),
  })
  telemetry?.setGzipSizeBytes({ bytes: gzipFile.size })

  if (process.env.NODE_ENV === 'development') {
    await measureDbSyncTelemetryPhase({
      telemetry,
      phase: 'db-backup.upload',
      task: async () => {
        const token = await requireToken(getToken)
        const body = new FormData()
        body.append('file', gzipFile)
        const url = new URL(
          `/v1/portfolio/${portfolioId}/db?timestamp=${timestamp}`,
          apiEndpoint,
        )
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body,
        })
        if (!response.ok) {
          throw await createApiError({
            action: 'upload portfolio DB file',
            response,
          })
        }
      },
    })
    await measureDbSyncTelemetryPhase({
      telemetry,
      phase: 'db-backup.complete',
      task: async () => {},
    })
    return
  }

  await measureDbSyncTelemetryPhase({
    telemetry,
    phase: 'db-backup.upload',
    task: async () => {
      const tokenForPresigned = await requireToken(getToken)
      const presignedResponse = await fetch(
        `${apiEndpoint}/v1/portfolio/${portfolioId}/db/upload/${timestamp}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${tokenForPresigned}`,
          },
        },
      )
      if (!presignedResponse.ok) {
        throw await createApiError({
          action: 'get presigned URL for portfolio DB file upload',
          response: presignedResponse,
        })
      }

      const presignedUrl = await presignedResponse.text()
      const uploadResponse = await fetch(presignedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/gzip',
        },
        body: gzipFile,
      })
      if (!uploadResponse.ok) {
        throw await createApiError({
          action: 'upload portfolio DB file',
          response: uploadResponse,
        })
      }
    },
  })

  await measureDbSyncTelemetryPhase({
    telemetry,
    phase: 'db-backup.complete',
    task: async () => {
      const tokenForUploaded = await requireToken(getToken)
      const uploadedResponse = await fetch(
        `${apiEndpoint}/v1/portfolio/${portfolioId}/db/uploaded/${timestamp}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${tokenForUploaded}`,
          },
        },
      )
      if (!uploadedResponse.ok) {
        throw await createApiError({
          action: 'notify portfolio DB file upload completion',
          response: uploadedResponse,
        })
      }
    },
  })
}
